/* eslint-disable max-lines-per-function */
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { preferencesAPI } from '@/lib/supabase/preferences';
import { buildAliasIndex, DealerConfigError, setDealerConfig } from '@/lib/supabase/dealerConfig';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { dealersKey, fetchDealersData } from './useDealers';
import { useIsInternalTenant } from './useIsInternalTenant';

interface DealerNotes {
  advisor_notes?: string | undefined;
  technician_notes?: string | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
  created_by?: string | undefined;
  updated_by?: string | undefined;
}

const DEALER_NOT_LISTED =
  "This dealer isn't in your dealer list yet. " +
  'Refresh dealer data on the Dealers page and try again.';
const DEMO_READ_ONLY = 'Dealer notes are fixed on demo accounts.';

/**
 * Resolves the canonical dealer id for a name shown on the map (any spelling),
 * from the cached Dealers list (fetched once if not cached yet).
 */
function useResolveDealerId(userId: string | undefined) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();
  return useCallback(
    async (dealerName: string): Promise<string> => {
      const { dealers } = await queryClient.fetchQuery({
        queryKey: dealersKey(userId, tenantId),
        queryFn: () => fetchDealersData(userId ?? '', tenantId),
        staleTime: 15 * 60 * 1000
      });
      const dealerId = buildAliasIndex(dealers).get(dealerName.trim());
      if (!dealerId) throw new DealerConfigError('unknown', DEALER_NOT_LISTED);
      return dealerId;
    },
    [queryClient, userId, tenantId]
  );
}

interface UseDealerNotesOptions {
  userId?: string | undefined;
  dealerNames: string[];
}

export const useDealerNotes = ({ userId, dealerNames }: UseDealerNotesOptions) => {
  const [notes, setNotes] = useState<Map<string, DealerNotes>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readOnly = useIsInternalTenant();
  const resolveDealerId = useResolveDealerId(userId);

  // Notes are tenant-level and written to every spelling by set_dealer_config.
  // technician_notes is mirrored into the legacy `notes` key: readers fall back
  // to `notes` when technician_notes is empty (getDealerNotesBatch, printouts).
  const writeNotes = useCallback(
    async (dealerName: string, advisorNotes?: string, technicianNotes?: string) => {
      if (readOnly) throw new DealerConfigError('permission', DEMO_READ_ONLY);
      const dealerId = await resolveDealerId(dealerName);
      await setDealerConfig([dealerId], {
        ...(advisorNotes !== undefined && { advisorNotes }),
        ...(technicianNotes !== undefined && { technicianNotes, notes: technicianNotes })
      });
    },
    [readOnly, resolveDealerId]
  );

  // OPTIMIZATION: Create stable string from dealer names to prevent unnecessary reloads
  // This prevents the flashing icon issue caused by array reference changes
  const dealerNamesKey = dealerNames.sort().join('|');

  // Load notes for given dealer names
  const loadNotes = useCallback(async () => {
    if (!userId || !dealerNames.length) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Batch load all dealer notes in a single request to avoid ERR_INSUFFICIENT_RESOURCES
      const notesData = await preferencesAPI.getDealerNotesBatch(userId, dealerNames);
      const notesMap = new Map<string, DealerNotes>();

      dealerNames.forEach(dealerName => {
        const dealerNotes = notesData[dealerName] || { advisor_notes: '', technician_notes: '' };
        notesMap.set(dealerName, {
          advisor_notes: dealerNotes.advisor_notes || '',
          technician_notes: dealerNotes.technician_notes || '',
          created_at: dealerNotes.created_at,
          updated_at: dealerNotes.updated_at,
          created_by: dealerNotes.created_by,
          updated_by: dealerNotes.updated_by
        });
      });

      setNotes(notesMap);
    } catch {
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dealerNamesKey]); // Use stable key, not dealerNames array

  // Get notes for a specific dealer
  const getNotes = useCallback(
    (dealerName: string): DealerNotes => {
      return notes.get(dealerName) || { advisor_notes: '', technician_notes: '' };
    },
    [notes]
  );

  // Check if dealer has any notes
  const hasNotes = useCallback(
    (dealerName: string) => {
      const dealerNotes = notes.get(dealerName);
      return !!(dealerNotes?.advisor_notes || dealerNotes?.technician_notes);
    },
    [notes]
  );

  // Check if dealer has advisor notes
  const hasAdvisorNotes = useCallback(
    (dealerName: string) => {
      const dealerNotes = notes.get(dealerName);
      return !!dealerNotes?.advisor_notes;
    },
    [notes]
  );

  // Check if dealer has technician notes
  const hasTechnicianNotes = useCallback(
    (dealerName: string) => {
      const dealerNotes = notes.get(dealerName);
      return !!dealerNotes?.technician_notes;
    },
    [notes]
  );

  // Update notes for a specific dealer
  const updateNotes = useCallback(
    async (
      dealerName: string,
      notesData: { advisor_notes?: string; technician_notes?: string }
    ) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      await writeNotes(dealerName, notesData.advisor_notes, notesData.technician_notes);

      // Update local state
      const currentNotes = notes.get(dealerName) || {};
      const updatedNotes = {
        ...currentNotes,
        ...notesData,
        updated_at: new Date().toISOString(),
        updated_by: userId
      };

      setNotes(prev => new Map(prev).set(dealerName, updatedNotes));
    },
    [userId, notes, writeNotes]
  );

  // Delete notes for a specific dealer
  const deleteNotes = useCallback(
    async (dealerName: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // '' clears a note (on every spelling, for every user)
      await writeNotes(dealerName, '', '');

      // Update local state
      setNotes(prev => {
        const newMap = new Map(prev);
        const dealerNotes = newMap.get(dealerName) || {};
        newMap.set(dealerName, {
          ...dealerNotes,
          advisor_notes: '',
          technician_notes: '',
          updated_at: new Date().toISOString(),
          updated_by: userId
        });
        return newMap;
      });
    },
    [userId, writeNotes]
  );

  // Load notes when dependencies change
  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  return {
    notes,
    loading,
    error,
    readOnly,
    loadNotes,
    getNotes,
    hasNotes,
    hasAdvisorNotes,
    hasTechnicianNotes,
    updateNotes,
    deleteNotes
  };
};

export default useDealerNotes;
